import DailyOverview from './components/DailyOverview';
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import SalespersonDetail from './components/SalespersonDetail';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import ComingSoon from './components/ComingSoon';
import CommissionsOverview from './components/CommissionsOverview';
import JQBookingsPage from './components/JQBookingsPage';
import ZenBookingsPage from './components/ZenBookingsPage';
import DepositReceiptAnnouncement from './components/DepositReceiptAnnouncement';
import ForecastModule from './components/ForecastModule';
import SyncStatus from './components/SyncStatus';
import BonusGate from './components/BonusGate';
import PnLStatement from './components/PnLStatement';
import MarketingLeadReport from './components/MarketingLeadReport';
import MarketingSpendReport from './components/MarketingSpendReport';
import MarketingLeads from './components/MarketingLeads';
import MarketingSpend from './components/MarketingSpend';
import KpiInsert from './components/KpiInsert';
import WfReport from './components/WfReport';
import JqCommissionGuide from './components/JqCommissionGuide';
import ZenCommissionGuide from './components/ZenCommissionGuide';
import CommissionGuide from './components/CommissionGuide';
import CommissionOperations from './components/CommissionOperations';
import SalesBreakdown from './components/SalesBreakdown';
import SalesTeamHub from './components/SalesTeamHub';
import ChangePassword from './components/ChangePassword';
import UsersAdmin from './components/UsersAdmin';
import BankAccountsSettings from './components/BankAccountsSettings';
import MeetingsList from './components/MeetingsList';
import MeetingDetail from './components/MeetingDetail';
import './App.css';
import './ui-polish.css';
import './lilac.css';

// Dark theme was removed; the app is light-only now. Clear any stale
// 'theme' preference left in localStorage from before so it doesn't linger.
try { localStorage.removeItem('theme'); } catch (e) { /* ignore */ }

function AppContent() {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.matchMedia?.('(max-width: 768px)').matches ?? false);
  const [syncKey, setSyncKey] = useState(0);
  const { user, token, loading, authError, retryVerification, logout, isAuthenticated, isSuperadmin, isAdmin, isMarketing, isSalesAdmin } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (authError) {
    return <div className="loading-screen" role="alert">
      <h1>Connection interrupted</h1>
      <p>{authError}</p>
      <button className="overview-button" onClick={retryVerification}>Try again</button>
      <button className="overview-link" onClick={logout}>Return to sign in</button>
    </div>;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const handleSyncComplete = () => {
    setSyncKey(prev => prev + 1);
  };

  return (
    <div className={`app-layout ${sidebarCollapsed ? 'nav-closed' : 'nav-open'}`}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        month={currentMonth}
        onNavigate={() => { if (window.matchMedia?.('(max-width: 768px)').matches) setSidebarCollapsed(true); }}
      />
      <div className={`main-area ${sidebarCollapsed ? 'expanded' : ''}`}>
        <TopBar
          month={currentMonth}
          setMonth={setCurrentMonth}
          onSyncComplete={handleSyncComplete}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed(value => !value)}
        />
        <main className="page-content">
          <Routes>
            {isSuperadmin ? (
              <>
                {/* Superadmin routes — full access (today's admin set) */}
                <Route path="/" element={<DailyOverview month={currentMonth} key={`overview-${syncKey}`} />} />
                <Route path="/overview/checks" element={<DailyOverview month={currentMonth} mode="checks" />} />
                <Route path="/overview/customers" element={<DailyOverview month={currentMonth} mode="customers" />} />
                <Route path="/overview/suppliers" element={<DailyOverview month={currentMonth} mode="suppliers" />} />
                <Route path="/analytics" element={<Dashboard month={currentMonth} initialTab="analytics" key={`analytics-${syncKey}`} />} />
                <Route path="/sales/team" element={<SalesTeamHub month={currentMonth} />} />
                <Route path="/salesperson/:id" element={<SalespersonDetail month={currentMonth} />} />
                <Route path="/jq-bookings" element={<JQBookingsPage month={currentMonth} />} />
                <Route path="/zen-bookings" element={<ZenBookingsPage month={currentMonth} />} />
                <Route path="/commissions" element={<CommissionsOverview month={currentMonth} />} />
                <Route path="/commission-control" element={<CommissionOperations month={currentMonth} />} />
                <Route path="/pnl" element={<PnLStatement month={currentMonth} view="simple" />} />
                <Route path="/pnl/detailed" element={<PnLStatement month={currentMonth} view="detailed" />} />
                <Route path="/pnl/compare" element={<PnLStatement month={currentMonth} view="compare" />} />
                <Route path="/marketing" element={<MarketingLeadReport month={currentMonth} />} />
                <Route path="/marketing/spend-report" element={<MarketingSpendReport month={currentMonth} />} />
                <Route path="/marketing/leads" element={<MarketingLeads month={currentMonth} />} />
                <Route path="/marketing/spend" element={<MarketingSpend month={currentMonth} />} />
                <Route path="/marketing/kpi" element={<KpiInsert month={currentMonth} />} />
                <Route path="/delivery-costs" element={<Navigate to="/pnl" replace />} />
                <Route path="/forecast" element={<ForecastModule month={currentMonth} />} />
                <Route path="/wf-report" element={<WfReport />} />
                <Route path="/bonus" element={<BonusGate />} />
                <Route path="/sync" element={<SyncStatus />} />
                <Route path="/jq-commission" element={<JqCommissionGuide />} />
                <Route path="/zen-commission" element={<ZenCommissionGuide />} />
                <Route path="/commission-guide/:id" element={<CommissionGuide month={currentMonth} />} />
                <Route path="/sales-breakdown/:id" element={<SalesBreakdown month={currentMonth} />} />
                <Route path="/settings/daily-forecast" element={<DailyOverview month={currentMonth} mode="settings" />} />
                <Route path="/settings/month-closing" element={<DailyOverview month={currentMonth} mode="closing" />} />
                <Route path="/settings/actuals" element={<ForecastModule month={currentMonth} mode="actuals" />} />
                <Route path="/settings/users" element={<UsersAdmin />} />
                <Route path="/settings/bank-accounts" element={<BankAccountsSettings />} />
                <Route path="/settings/api" element={<ComingSoon />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/meetings" element={<MeetingsList />} />
                <Route path="/meetings/:id" element={<MeetingDetail />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : isAdmin ? (
              <>
                {/* Admin routes — view-only subset. Hidden: marketing, delivery,
                    bonus, all /settings/*, sync triggers. Unknown paths → Dashboard. */}
                <Route path="/" element={<DailyOverview month={currentMonth} key={`overview-${syncKey}`} />} />
                <Route path="/overview/checks" element={<DailyOverview month={currentMonth} mode="checks" />} />
                <Route path="/overview/customers" element={<DailyOverview month={currentMonth} mode="customers" />} />
                <Route path="/overview/suppliers" element={<DailyOverview month={currentMonth} mode="suppliers" />} />
                <Route path="/analytics" element={<Dashboard month={currentMonth} initialTab="analytics" key={`analytics-${syncKey}`} />} />
                <Route path="/sales/team" element={<SalesTeamHub month={currentMonth} />} />
                <Route path="/salesperson/:id" element={<SalespersonDetail month={currentMonth} />} />
                <Route path="/jq-bookings" element={<JQBookingsPage month={currentMonth} />} />
                <Route path="/zen-bookings" element={<ZenBookingsPage month={currentMonth} />} />
                <Route path="/commissions" element={<CommissionsOverview month={currentMonth} />} />
                <Route path="/commission-control" element={<CommissionOperations month={currentMonth} />} />
                <Route path="/pnl" element={<PnLStatement month={currentMonth} view="simple" />} />
                <Route path="/pnl/detailed" element={<PnLStatement month={currentMonth} view="detailed" />} />
                <Route path="/pnl/compare" element={<PnLStatement month={currentMonth} view="compare" />} />
                <Route path="/marketing/kpi" element={<KpiInsert month={currentMonth} />} />
                <Route path="/forecast" element={<ForecastModule month={currentMonth} />} />
                <Route path="/sync" element={<SyncStatus />} />
                <Route path="/jq-commission" element={<JqCommissionGuide />} />
                <Route path="/zen-commission" element={<ZenCommissionGuide />} />
                <Route path="/commission-guide/:id" element={<CommissionGuide month={currentMonth} />} />
                <Route path="/sales-breakdown/:id" element={<SalesBreakdown month={currentMonth} />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/meetings" element={<MeetingsList />} />
                <Route path="/meetings/:id" element={<MeetingDetail />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : isMarketing ? (
              <>
                {/* Marketing routes — lead + spend key-in and their reports only. */}
                <Route path="/marketing" element={<MarketingLeadReport month={currentMonth} />} />
                <Route path="/marketing/leads" element={<MarketingLeads month={currentMonth} />} />
                <Route path="/marketing/spend-report" element={<MarketingSpendReport month={currentMonth} />} />
                <Route path="/marketing/spend" element={<MarketingSpend month={currentMonth} />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="*" element={<Navigate to="/marketing/leads" replace />} />
              </>
            ) : isSalesAdmin ? (
              <>
                {/* Sales admin — RMC lead key-in only. MarketingLeads locks the
                    project to RMC for this role; the backend enforces the same. */}
                <Route path="/marketing/leads" element={<MarketingLeads month={currentMonth} />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="*" element={<Navigate to="/marketing/leads" replace />} />
              </>
            ) : (
              <>
                {/* Salesperson routes — only their own page */}
                <Route path="/salesperson/:id" element={<SalespersonDetail month={currentMonth} />} />
                {/* JQ pages are JQ-only — other salespeople would only hit 403s there. */}
                <Route path="/jq-bookings" element={(user?.salesperson_name || user?.name || '').trim().toLowerCase() === 'jq' ? <JQBookingsPage month={currentMonth} /> : <Navigate to={`/salesperson/${user?.salesperson_id}`} replace />} />
                <Route path="/zen-bookings" element={(user?.salesperson_name || user?.name || '').trim().toLowerCase() === 'zen' ? <ZenBookingsPage month={currentMonth} /> : <Navigate to={`/salesperson/${user?.salesperson_id}`} replace />} />
                <Route path="/jq-commission" element={(user?.salesperson_name || user?.name || '').trim().toLowerCase() === 'jq' ? <JqCommissionGuide /> : <Navigate to={`/salesperson/${user?.salesperson_id}`} replace />} />
                <Route path="/zen-commission" element={(user?.salesperson_name || user?.name || '').trim().toLowerCase() === 'zen' ? <ZenCommissionGuide /> : <Navigate to={`/salesperson/${user?.salesperson_id}`} replace />} />
                <Route path="/commission-guide/:id" element={<CommissionGuide month={currentMonth} />} />
                <Route path="/sales-breakdown/:id" element={<SalesBreakdown month={currentMonth} />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/meetings" element={<MeetingsList />} />
                <Route path="/meetings/:id" element={<MeetingDetail />} />
                <Route path="*" element={<Navigate to={`/salesperson/${user?.salesperson_id}`} replace />} />
              </>
            )}
          </Routes>
        </main>
      </div>
      <DepositReceiptAnnouncement user={user} token={token} />
    </div>
  );
}

// Native <input type="date"> only opens its calendar when you hit the small icon at
// the right edge — clicking the field itself does nothing, which reads as a dead box.
// One delegated listener makes the whole field open the picker, and covers every date
// input in the app (JQ/Zen bookings, meetings, marketing leads) including ones added
// later. showPicker() throws if the picker is already open or the browser lacks it,
// so failures fall back to the default behaviour rather than breaking the click.
function useDatePickerOnFieldClick() {
  React.useEffect(() => {
    const openPicker = (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement) || el.type !== 'date') return;
      if (el.readOnly || el.disabled) return;
      try {
        el.showPicker();
      } catch {
        /* already open, or unsupported — leave the native behaviour alone */
      }
    };
    document.addEventListener('click', openPicker);
    return () => document.removeEventListener('click', openPicker);
  }, []);
}

function App() {
  useDatePickerOnFieldClick();
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
